import { ArrowRight, Layers } from 'lucide-react'
import type { ReactNode } from 'react'

import type { LoanAccountDetail, LoanCycleSummary } from '@/api/loans-types'
import { MoneyDisplay } from '@/components/money-display'
import { StatusBadge } from '@/components/status-badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatShortDate } from '@/lib/datetime'
import { useIsAtLeast } from '@/lib/use-viewport'

import { cycleStatusLabel } from './constants'
import { LoanLink } from './loan-link'
import { loanPaths } from './loan-paths'
import { EmptyPanel, Field, Section } from './panel-primitives'

/**
 * The account, which is the permanent thing.
 *
 * Its number never changes and it carries no balance: a loan account is a file, and every figure
 * belongs to one of the rounds inside it. That is how the credit bureau extract reports it too,
 * so the two are never merged into a single "loan" here.
 */
export function LoanAccountOverviewPanel({ account }: { account: LoanAccountDetail }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title="Account" description="Permanent. The number outlives every round on it.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Account number" value={account.accountNumber} mono />
          <Field label="Opened" value={formatShortDate(account.openedDate)} />
          <Field
            label="Closed"
            value={account.closedDate === null ? null : formatShortDate(account.closedDate)}
          />
          <Field label="Rounds completed" value={account.totalCyclesCompleted} />
          <Field label="Product" value={account.productName} />
          <Field label="Branch" value={`Branch ${account.branchId}`} />
        </dl>
      </Section>

      <Section title="Member" description="One person, one file — whichever branch they walk into.">
        <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
          <Field label="Customer" value={account.customerName} />
          <Field label="Customer id" value={account.customerId} mono />
          <Field
            label="Group"
            value={account.groupId}
            mono
            hint={
              account.groupId === null
                ? 'An individual loan. Some products lend only to a group.'
                : undefined
            }
          />
        </dl>
      </Section>

      <Section
        title="What the gates were met with"
        description="Held on the account as it stood at opening. The live check is on the prerequisites tab."
        className="lg:col-span-2"
      >
        <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          <Field
            label="Savings account"
            value={account.savingsAccountNumber}
            mono
            hint={
              account.savingsAccountNumber === null
                ? 'Not named on the account record.'
                : undefined
            }
          />
          <Field
            label="Security deposit account"
            value={account.securityDepositAccountNumber}
            mono
            hint={
              account.securityDepositAccountNumber === null
                ? 'Not named on the account record.'
                : undefined
            }
          />
          <Field label="Passbook" value={account.passbookNumber} mono />
        </dl>
      </Section>
    </div>
  )
}

/**
 * Every round on the account, newest first.
 *
 * A round is a lending; the account is the file it sits in. Opening one is how every command on
 * this area is reached, so the whole row is a link rather than a chevron somebody has to aim at.
 *
 * Below `lg` the table becomes stacked cards, as docs/mobile-policy.md requires of every data
 * table: this screen adapts rather than refusing, and a nine-column round table left as a table
 * would make the page itself scroll sideways on a phone — which the policy rules out in as many
 * words. The card leads with the round and its status, because that is what somebody ringing the
 * branch is asking about, and carries the same figures as labelled pairs.
 */
export function LoanAccountCyclesPanel({
  account,
  action,
}: {
  account: LoanAccountDetail
  /** The "apply for the next round" control, gated by the screen that owns it. */
  action?: ReactNode
}) {
  const isWide = useIsAtLeast('lg')
  const cycles = [...account.cycles].sort((left, right) => right.cycleNumber - left.cycleNumber)

  if (cycles.length === 0) {
    return (
      <EmptyPanel
        icon={<Layers className="size-6" />}
        title="No rounds yet"
        description="The account exists and the gates were met at opening. The first round starts with an application."
        action={action}
      />
    )
  }

  return (
    <Section title="Rounds" description="Newest first. Every figure on a loan belongs to a round." actions={action}>
      {isWide ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Round</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Principal</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
              <TableHead className="text-right">In arrears</TableHead>
              <TableHead>Disbursed</TableHead>
              <TableHead>Matures</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {cycles.map((cycle) => (
              <CycleRow key={cycle.id} accountId={account.id} cycle={cycle} />
            ))}
          </TableBody>
        </Table>
      ) : (
        <ul className="space-y-3">
          {cycles.map((cycle) => (
            <li key={cycle.id}>
              <CycleCard accountId={account.id} cycle={cycle} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

/** One round as a card, for the narrow layout. The whole card is the link into the round. */
function CycleCard({ accountId, cycle }: { accountId: string; cycle: LoanCycleSummary }) {
  return (
    <LoanLink
      to={loanPaths.cycle(accountId, cycle.id)}
      className="block rounded-card border border-border-subtle bg-surface p-3 hover:border-border-strong"
      aria-label={`Open round ${cycle.displayReference}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-heading">Round {cycle.cycleNumber}</p>
          <p className="font-mono text-2xs text-muted">{cycle.displayReference}</p>
        </div>
        <div className="text-right">
          <StatusBadge status={cycle.status} />
          <p className="mt-0.5 text-2xs text-muted">{cycleStatusLabel(cycle.status)}</p>
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
        <Field
          label="Principal"
          value={
            <MoneyDisplay
              value={cycle.principalMinor}
              currency={cycle.currencyCode}
              className="text-left"
            />
          }
        />
        <Field
          label="Outstanding"
          value={
            <MoneyDisplay
              value={cycle.totalOutstandingMinor}
              currency={cycle.currencyCode}
              className="text-left"
            />
          }
        />
        <Field
          label="In arrears"
          value={
            <MoneyDisplay
              value={cycle.amountInArrearsMinor}
              currency={cycle.currencyCode}
              className="text-left"
            />
          }
          hint={cycle.daysInArrears > 0 ? `${cycle.daysInArrears} days behind` : undefined}
        />
        <Field
          label="Disbursed"
          value={
            cycle.disbursementDate === null ? null : formatShortDate(cycle.disbursementDate)
          }
        />
        <Field
          label="Matures"
          value={cycle.maturityDate === null ? null : formatShortDate(cycle.maturityDate)}
        />
      </dl>

      <span className="mt-3 inline-flex min-h-11 items-center gap-1 text-xs font-medium text-brand-text">
        Open the round
        <ArrowRight className="size-3.5" aria-hidden />
      </span>
    </LoanLink>
  )
}

function CycleRow({ accountId, cycle }: { accountId: string; cycle: LoanCycleSummary }) {
  return (
    <TableRow>
      <TableCell className="numeric tabular-nums">{cycle.cycleNumber}</TableCell>
      <TableCell className="font-mono text-xs">{cycle.displayReference}</TableCell>
      <TableCell className="whitespace-nowrap">
        <StatusBadge status={cycle.status} />
        <p className="mt-0.5 text-2xs text-muted">{cycleStatusLabel(cycle.status)}</p>
      </TableCell>
      <TableCell>
        <MoneyDisplay value={cycle.principalMinor} currency={cycle.currencyCode} symbol={false} />
      </TableCell>
      <TableCell>
        <MoneyDisplay
          value={cycle.totalOutstandingMinor}
          currency={cycle.currencyCode}
          symbol={false}
        />
      </TableCell>
      <TableCell>
        <div className="space-y-0.5">
          <MoneyDisplay
            value={cycle.amountInArrearsMinor}
            currency={cycle.currencyCode}
            symbol={false}
          />
          {cycle.daysInArrears > 0 ? (
            <p className="text-2xs whitespace-nowrap text-danger-text">
              {cycle.daysInArrears} days
            </p>
          ) : null}
        </div>
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {cycle.disbursementDate === null ? (
          <span className="text-muted">—</span>
        ) : (
          formatShortDate(cycle.disbursementDate)
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {cycle.maturityDate === null ? (
          <span className="text-muted">—</span>
        ) : (
          formatShortDate(cycle.maturityDate)
        )}
      </TableCell>
      <TableCell>
        <LoanLink
          to={loanPaths.cycle(accountId, cycle.id)}
          className="inline-flex items-center gap-1 text-xs font-medium text-brand-text hover:text-brand-text-hover"
          aria-label={`Open round ${cycle.displayReference}`}
        >
          Open
          <ArrowRight className="size-3.5" />
        </LoanLink>
      </TableCell>
    </TableRow>
  )
}
